<template>
  <div class="manual-venue-entry">
    <h4>Add Venue Manually</h4>
    <div class="field">
      <label class="label">Venue Name</label>
      <div class="control">
        <input
          class="input"
          type="text"
          placeholder="Venue name"
          v-model="venue.name"
          required
        />
      </div>
    </div>

    <div class="field">
      <label class="label">Venue Type</label>
      <div class="control">
        <div class="select">
          <select v-model="venue.type">
            <option value="">Select a type...</option>
            <option>Theater</option>
            <option>Live Music</option>
            <option>Burlesque</option>
            <option>Comedy</option>
            <option>Cafe</option>
            <option>Cabaret/Variety</option>
            <option>Event Space</option>
            <option>Mixed Use</option>
          </select>
        </div>
      </div>
    </div>

    <div class="field">
      <label class="label">Address</label>
      <div class="control has-icons-left">
        <GmapAutocomplete
          class="input"
          placeholder="Search for address or enter manually"
          @place_changed="setAddressFromGoogle"
          :value="venue.address"
          @input="onAddressInput"
          :options="autocompleteOptions"
        />
        <span class="icon is-small is-left">
          <i class="fas fa-search"></i>
        </span>
      </div>
      <p class="help">Start typing to search, or enter the address manually</p>
    </div>

    <div class="columns">
      <div class="column">
        <div class="field">
          <label class="label">City</label>
          <div class="control">
            <input
              class="input"
              type="text"
              placeholder="City"
              v-model="venue.city"
            />
          </div>
        </div>
      </div>
      <div class="column is-narrow">
        <div class="field">
          <label class="label">State/Province</label>
          <div class="control">
            <div class="select">
              <select v-model="venue.state">
                <option value="">Select...</option>
                <optgroup label="United States">
                  <option
                    v-for="state in usStates"
                    :key="state.abbr"
                    :value="state.name"
                  >
                    {{ state.name }}
                  </option>
                </optgroup>
                <optgroup label="Canada">
                  <option
                    v-for="province in canadaProvinces"
                    :key="province.abbr"
                    :value="province.name"
                  >
                    {{ province.name }}
                  </option>
                </optgroup>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="columns">
      <div class="column is-narrow">
        <div class="field">
          <label class="label">Zip/Postal Code</label>
          <div class="control">
            <input
              class="input"
              type="text"
              placeholder="Zip/Postal Code"
              v-model="venue.zip"
              style="width: 150px;"
            />
          </div>
        </div>
      </div>
      <div class="column is-narrow">
        <div class="field">
          <label class="label">Country</label>
          <div class="control">
            <div class="select">
              <select v-model="venue.country">
                <option value="">Select...</option>
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <div class="field">
          <label class="label">Website</label>
          <div class="control">
            <input
              class="input"
              type="url"
              placeholder="https://example.com"
              v-model="venue.website"
            />
          </div>
        </div>
      </div>
      <div class="column is-narrow">
        <div class="field">
          <label class="label">Phone</label>
          <div class="control">
            <input
              class="input"
              type="tel"
              placeholder="(555) 555-5555"
              v-model="venue.phone"
              style="width: 180px;"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="buttons">
      <button class="button" @click="cancel">Cancel</button>
      <button class="button is-primary" @click="saveVenue" :disabled="!isValid">
        Save Venue
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "ManualVenueEntry",
  data() {
    return {
      venue: {
        name: "",
        type: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        website: "",
        phone: "",
        location: null,
        fullAddress: ""
      },
      addressFromGoogle: false,
      autocompleteOptions: {
        componentRestrictions: { country: ["us", "ca"] }
      },
      usStates: [
        { abbr: "AL", name: "Alabama" },
        { abbr: "AK", name: "Alaska" },
        { abbr: "AZ", name: "Arizona" },
        { abbr: "AR", name: "Arkansas" },
        { abbr: "CA", name: "California" },
        { abbr: "CO", name: "Colorado" },
        { abbr: "CT", name: "Connecticut" },
        { abbr: "DE", name: "Delaware" },
        { abbr: "DC", name: "District of Columbia" },
        { abbr: "FL", name: "Florida" },
        { abbr: "GA", name: "Georgia" },
        { abbr: "HI", name: "Hawaii" },
        { abbr: "ID", name: "Idaho" },
        { abbr: "IL", name: "Illinois" },
        { abbr: "IN", name: "Indiana" },
        { abbr: "IA", name: "Iowa" },
        { abbr: "KS", name: "Kansas" },
        { abbr: "KY", name: "Kentucky" },
        { abbr: "LA", name: "Louisiana" },
        { abbr: "ME", name: "Maine" },
        { abbr: "MD", name: "Maryland" },
        { abbr: "MA", name: "Massachusetts" },
        { abbr: "MI", name: "Michigan" },
        { abbr: "MN", name: "Minnesota" },
        { abbr: "MS", name: "Mississippi" },
        { abbr: "MO", name: "Missouri" },
        { abbr: "MT", name: "Montana" },
        { abbr: "NE", name: "Nebraska" },
        { abbr: "NV", name: "Nevada" },
        { abbr: "NH", name: "New Hampshire" },
        { abbr: "NJ", name: "New Jersey" },
        { abbr: "NM", name: "New Mexico" },
        { abbr: "NY", name: "New York" },
        { abbr: "NC", name: "North Carolina" },
        { abbr: "ND", name: "North Dakota" },
        { abbr: "OH", name: "Ohio" },
        { abbr: "OK", name: "Oklahoma" },
        { abbr: "OR", name: "Oregon" },
        { abbr: "PA", name: "Pennsylvania" },
        { abbr: "RI", name: "Rhode Island" },
        { abbr: "SC", name: "South Carolina" },
        { abbr: "SD", name: "South Dakota" },
        { abbr: "TN", name: "Tennessee" },
        { abbr: "TX", name: "Texas" },
        { abbr: "UT", name: "Utah" },
        { abbr: "VT", name: "Vermont" },
        { abbr: "VA", name: "Virginia" },
        { abbr: "WA", name: "Washington" },
        { abbr: "WV", name: "West Virginia" },
        { abbr: "WI", name: "Wisconsin" },
        { abbr: "WY", name: "Wyoming" }
      ],
      canadaProvinces: [
        { abbr: "AB", name: "Alberta" },
        { abbr: "BC", name: "British Columbia" },
        { abbr: "MB", name: "Manitoba" },
        { abbr: "NB", name: "New Brunswick" },
        { abbr: "NL", name: "Newfoundland and Labrador" },
        { abbr: "NS", name: "Nova Scotia" },
        { abbr: "NT", name: "Northwest Territories" },
        { abbr: "NU", name: "Nunavut" },
        { abbr: "ON", name: "Ontario" },
        { abbr: "PE", name: "Prince Edward Island" },
        { abbr: "QC", name: "Quebec" },
        { abbr: "SK", name: "Saskatchewan" },
        { abbr: "YT", name: "Yukon" }
      ]
    };
  },
  computed: {
    isValid() {
      return (
        this.venue.name &&
        this.venue.address &&
        this.venue.city &&
        this.venue.state &&
        this.venue.zip &&
        this.venue.country
      );
    }
  },
  methods: {
    onAddressInput(event) {
      // When user types manually, update the address
      if (event && event.target) {
        this.venue.address = event.target.value;
        this.addressFromGoogle = false;
      }
    },
    setAddressFromGoogle(place) {
      console.log("Google Places response:", place);

      if (!place || !place.address_components) {
        return;
      }

      this.addressFromGoogle = true;

      // Extract address components
      const components = place.address_components;

      // Find each component type
      const streetNumber = components.find(c =>
        c.types.includes("street_number")
      );
      const route = components.find(c => c.types.includes("route"));
      const city =
        components.find(c => c.types.includes("locality")) ||
        components.find(c => c.types.includes("sublocality")) ||
        components.find(c => c.types.includes("neighborhood"));
      const state = components.find(c =>
        c.types.includes("administrative_area_level_1")
      );
      const country = components.find(c => c.types.includes("country"));
      const postalCode = components.find(c => c.types.includes("postal_code"));

      // Build street address
      let streetAddress = "";
      if (streetNumber) streetAddress += streetNumber.long_name + " ";
      if (route) streetAddress += route.long_name;

      // Update venue fields
      this.venue.address = streetAddress.trim() || place.formatted_address;
      this.venue.fullAddress = place.formatted_address;

      if (city) this.venue.city = city.long_name;
      if (state) this.venue.state = state.long_name;
      if (country) this.venue.country = country.long_name;
      if (postalCode) this.venue.zip = postalCode.long_name;

      // Store location coordinates if available
      if (place.geometry && place.geometry.location) {
        this.venue.location = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
      }

      // If place has a name that's different from the address, use it
      if (
        place.name &&
        place.name !== streetAddress.trim() &&
        !this.venue.name
      ) {
        this.venue.name = place.name;
      }

      // Get website and phone if available
      if (place.website && !this.venue.website) {
        this.venue.website = place.website;
      }
      if (place.formatted_phone_number && !this.venue.phone) {
        this.venue.phone = place.formatted_phone_number;
      }
    },
    cancel() {
      this.$emit("cancel");
    },
    saveVenue() {
      if (!this.isValid) return;

      // Build full address if not from Google
      if (!this.venue.fullAddress) {
        this.venue.fullAddress = `${this.venue.address}, ${this.venue.city}, ${this.venue.state} ${this.venue.zip}, ${this.venue.country}`;
      }

      const venueData = {
        name: this.venue.name,
        type: this.venue.type || null,
        address: this.venue.address,
        city: this.venue.city,
        state: this.venue.state,
        zip: this.venue.zip,
        country: this.venue.country,
        fullAddress: this.venue.fullAddress,
        website: this.venue.website || null,
        phone: this.venue.phone || null,
        location: this.venue.location || null,
        manualEntry: true
      };

      this.$emit("venueCreated", venueData);
    }
  }
};
</script>

<style scoped>
.manual-venue-entry {
  padding: 15px 0;
}
.manual-venue-entry h4 {
  margin-bottom: 20px;
}
.buttons {
  margin-top: 20px;
}
</style>
